import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useBreakpoint } from "../hooks/useBreakpoint";
import ProductDetailModal from "../components/ProductDetailModal";
import RowActionsMenu, { IconOpen, IconTrash } from "../components/RowActionsMenu";
import MarketedBadge from "../components/MarketedBadge";
import ProductNameCell from "../components/ProductNameCell";
import { ConfirmDialog } from "../components/ui";
import { useT } from "../i18n";
import { uniqueSorted, filterPortfolio, filterCompetitors, type SinComercializar } from "../lib/portfolioFilters";
import { useAuthStore } from "../store/authStore";
import HierarchyFilter from "../components/HierarchyFilter";
import ExportButtons from "../components/ExportButtons";
import SearchInput from "../components/SearchInput";
import ParallelImportsPanel from "../components/ParallelImportsPanel";
import SuggestInput from "../components/SuggestInput";
type PF = { id:string; cn_code:string; product_name:string; active_ingredient:string; therapeutic_area:string|null; atc_letter:string|null; laboratory:string|null; is_active:boolean; is_comercializado:boolean|null; sin_comercializar?: SinComercializar|null };
type Comp = { id:string; cn_code:string|null; company_name:string|null; product_name:string|null; active_ingredient:string; therapeutic_area:string|null; atc_letter:string|null; competitor_type:string; is_active:boolean; origin:string|null; created_at?:string|null };

const inp = (extra?:React.CSSProperties):React.CSSProperties => ({ background:"var(--clr-input-bg)", border:"1px solid var(--clr-border)", borderRadius:12, color:"var(--clr-text)", padding:"0.5rem 0.85rem", fontSize:"0.82rem", outline:"none", width:"100%", boxSizing:"border-box" as const, ...extra });
const btn = (bg:string):React.CSSProperties => ({ background:bg, color:"#fff", border:0, borderRadius:999, padding:"0.5rem 1.3rem", fontSize:"0.8rem", fontWeight:600, cursor:"pointer" });
// Anchos fijos: `tableLayout:"fixed"` es lo que permite truncar el nombre del
// producto con «…». La columna Producto no lleva ancho: se queda el sobrante.
// Por eso la tabla necesita un minWidth mayor que la suma del resto — si no,
// a Producto le tocarían 0px y el nombre no se vería.
// Las dos tablas no necesitan lo mismo:
//  · Mis Productos — el área trae los nombres ATC largos («Antineoplásicos e
//    inmunomoduladores», 35 car.) y el titular casi siempre es el mismo.
//  · Competidores  — el área viene del mapeo corto de `utils/labs.py`
//    («Digestivo y metabolismo») pero el laboratorio es LO importante: 60
//    distintos y hasta 43 caracteres, y a 170 px se cortaba siempre.
const COL_DESKTOP = { cn:96, ingredient:200, area:250, lab:170,
                      areaComp:200, labComp:230,
                      status:150, type:120, actions:48 } as const;
const COL_MOBILE  = { cn:84, ingredient:140, area:0,   lab:0,
                      areaComp:0, labComp:0,
                      status:124, type:104, actions:40 } as const;
const TABLE_MIN_DESKTOP = 900;   // sobran ~186px para Producto
const TABLE_MIN_MOBILE  = 620;   // sobran ~272px para Producto
/** Área terapéutica con la letra del grupo ATC delante.
 *  Las dos tablas guardaron el área con vocabularios distintos («Aparato
 *  locomotor» / «Musculoesquelético»); la letra hace evidente que son el mismo
 *  grupo, y es como habla alguien de acceso al mercado. Pendiente de que Carlos
 *  valide la lista de 14 nombres para unificarlos de verdad. */
function AreaCell({ area, letter }: { area: string|null; letter: string|null }) {
  if (!area && !letter) return <>–</>;
  return (
    <span style={{ display:"inline-flex", alignItems:"baseline", gap:6, minWidth:0 }}>
      {letter && (
        <span title={`Grupo ATC ${letter}`}
          style={{ fontSize:"0.68rem", fontWeight:700, color:"var(--clr-text3)",
                   fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", flexShrink:0 }}>
          {letter}<span style={{ opacity:0.5, margin:"0 1px" }}>·</span>
        </span>
      )}
      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{area || "–"}</span>
    </span>
  );
}

const thBase: React.CSSProperties = {
  padding:"0.6rem 10px", textAlign:"left", fontSize:"0.65rem", fontWeight:600,
  textTransform:"uppercase", letterSpacing:0.9, color:"var(--clr-text4)", whiteSpace:"nowrap",
};

// Chip de los competidores que HelixScan detectó al entrar el genérico al mercado.
// Rosa: el mismo color que el tipo de cambio `marketing_status` en Cambios/Histórico.
const MARKET_ENTRY = "#ec4899";
function NewToMarketChip({ label, title }: { label:string; title:string }) {
  return (
    <span title={title} style={{
      display:"inline-flex", alignItems:"center", gap:5,
      background:MARKET_ENTRY+"1e", color:MARKET_ENTRY, border:`1px solid ${MARKET_ENTRY}33`,
      borderRadius:999, padding:"2px 9px", fontSize:"0.66rem", fontWeight:600, whiteSpace:"nowrap",
    }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink:0 }}>
        <path d="M12 19V5M5 12l7-7 7 7" />
      </svg>
      {label}
    </span>
  );
}

// Flecha "abrir ficha" (reemplaza el emoji ↗)
const OpenIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink:0 }}>
    <path d="M7 17L17 7" /><path d="M8 7h9v9" />
  </svg>
);

// Reglas de universo por jerarquía (R-04/R-06/R-07, §90 Fase 2B): laboratorio,
// principio activo, ATC, agrupación homogénea o conjunto SPR. Una regla es viva:
// un CN nuevo que encaje entra solo, sin alta manual.
type Scope = { id:string; kind:string; value:string; side:"portfolio"|"competitor"; label:string|null; count:number|null };
const SCOPE_KINDS = ["lab", "pa", "atc", "ah", "spr"] as const;

function ScopeRules({ side, canEdit }: { side:"portfolio"|"competitor"; canEdit:boolean }) {
  const t = useT();
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [kind, setKind] = useState<string>("lab");
  const [value, setValue] = useState("");
  const [preview, setPreview] = useState<{ count:number; sample:{ cn:string; nombre:string }[] } | null>(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => api.get<{ items:Scope[] }>("/portfolio/scopes").then(r => setScopes(r.items.filter(s => s.side === side))).catch(() => {});
  useEffect(() => { load(); }, [side]);

  // Sugerencias y previsualización con retardo: cada pulsación no es una petición.
  useEffect(() => {
    if (value.trim().length < 2) { setPreview(null); return; }
    const h = setTimeout(() => {
      api.get<{ count:number; sample:{ cn:string; nombre:string }[] }>(`/portfolio/scopes/preview?kind=${kind}&value=${encodeURIComponent(value.trim())}`)
        .then(setPreview).catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(h);
  }, [kind, value]);

  const add = async () => {
    setErr("");
    try {
      await api.post("/portfolio/scopes", { kind, value: value.trim(), side });
      setValue(""); setPreview(null); load();
    } catch (e:any) { setErr(e.message); }
  };
  const remove = async (id:string) => { try { await api.delete(`/portfolio/scopes/${id}`); load(); } catch (e:any) { setErr(e.message); } };

  const kindLabel = (k:string) => t(`portfolio.scopeKind_${k}`);
  return (
    <div style={{ background:"var(--clr-surface)", border:"1px solid var(--clr-border)", borderRadius:18, backdropFilter:"var(--clr-blur)", WebkitBackdropFilter:"var(--clr-blur)", boxShadow:"var(--clr-shadow-sm)", padding:"0.9rem 1.25rem", marginBottom:16 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
        <span style={{ fontWeight:600, fontSize:"0.9rem" }}>{t("portfolio.scopesTitle")}</span>
        <span style={{ fontSize:"0.72rem", color:"var(--clr-text4)" }}>
          {scopes.length === 0 ? t("portfolio.scopesNone") : t("portfolio.scopesCount", { n: scopes.length, cn: scopes.reduce((a, s) => a + (s.count ?? 0), 0) })}
        </span>
        <span style={{ marginLeft:"auto", color:"var(--clr-text4)", fontSize:"0.75rem" }}>{open ? "▴" : "▾"}</span>
      </div>
      {open && (
        <div style={{ marginTop:12 }}>
          <div style={{ fontSize:"0.75rem", color:"var(--clr-text3)", marginBottom:10, lineHeight:1.5 }}>
            {side === "portfolio" ? t("portfolio.scopesHintPortfolio") : t("portfolio.scopesHintCompetitor")}
          </div>
          {scopes.length > 0 && (
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.8rem", marginBottom:12 }}>
              <tbody>
                {scopes.map(s => (
                  <tr key={s.id} style={{ borderTop:"1px solid var(--clr-border2)" }}>
                    <td style={{ padding:"6px 8px", color:"var(--clr-text4)", whiteSpace:"nowrap", width:150 }}>{kindLabel(s.kind)}</td>
                    <td style={{ padding:"6px 8px", fontWeight:600 }}>
                      {s.value}
                      {s.label && s.label !== s.value && <span style={{ fontWeight:400, color:"var(--clr-text3)", marginLeft:8 }}>{s.label}</span>}
                    </td>
                    <td style={{ padding:"6px 8px", color:"var(--clr-text3)", whiteSpace:"nowrap", textAlign:"right" }}>{t("portfolio.scopeCn", { n: s.count ?? 0 })}</td>
                    {canEdit && <td style={{ padding:"6px 8px", width:40, textAlign:"right" }}>
                      <button onClick={() => remove(s.id)} title={t("portfolio.scopeRemove")} style={{ background:"transparent", border:0, color:"var(--clr-text4)", cursor:"pointer", fontSize:"0.9rem" }}>×</button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {canEdit && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
              <select value={kind} onChange={e => { setKind(e.target.value); setValue(""); }} style={{ ...inp({ borderRadius:999 }), width:"auto" }}>
                {SCOPE_KINDS.map(k => <option key={k} value={k}>{kindLabel(k)}</option>)}
              </select>
              <SuggestInput kind={kind} value={value} onChange={setValue}
                placeholder={t(`portfolio.scopePlaceholder_${kind}`)} style={{ ...inp({ borderRadius:999 }), width:340, maxWidth:"100%" }} />
              {preview && (
                <span style={{ fontSize:"0.78rem", color: preview.count > 0 ? "var(--clr-text2)" : "var(--clr-text4)" }} title={preview.sample.map(x => `${x.cn} ${x.nombre}`).join("\n")}>
                  → {t("portfolio.scopeCn", { n: preview.count })}
                </span>
              )}
              <button onClick={add} disabled={!preview || preview.count === 0} style={{ ...btn("var(--clr-org-primary)"), opacity: (!preview || preview.count === 0) ? 0.5 : 1 }}>{t("portfolio.scopeAdd")}</button>
              {err && <span style={{ color:"#f87171", fontSize:"0.78rem" }}>{err}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type Mode = "portfolio" | "competitors";
export default function PortfolioPage({ mode = "portfolio" }: { mode?: Mode }) {
  const t = useT();
  const tab: Mode = mode;
  const { isMobile } = useBreakpoint();
  const COL = isMobile ? COL_MOBILE : COL_DESKTOP;
  const tableMin = isMobile ? TABLE_MIN_MOBILE : TABLE_MIN_DESKTOP;
  const [items, setItems] = useState<PF[]>([]);
  const [comps, setComps] = useState<Comp[]>([]);
  const [pfTotal, setPfTotal] = useState(0);
  const [compTotal, setCompTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [hier, setHier] = useState("");          // R-35: atc|ah|spr|pa|nregistro:valor
  const [compTypeFilter, setCompTypeFilter] = useState<string>("");
  const [areaFilter, setAreaFilter] = useState<string>("");
  const [labFilter, setLabFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [compAreaFilter, setCompAreaFilter] = useState<string>("");
  const [compCompanyFilter, setCompCompanyFilter] = useState<string>("");
  const [modal, setModal] = useState<null|"pf_add"|"comp_add">(null);
  const [form, setForm] = useState<Record<string,string>>({});
  const [msg, setMsg] = useState("");
  const [detailCn, setDetailCn] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{kind:"pf"|"comp"; id:string}|null>(null);
  // Bajas en bloque (contrato con Italfarmaco, punto 9): los que ves con los filtros puestos.
  const [pendingBulk, setPendingBulk] = useState<{side:"portfolio"|"competitor"; ids:string[]}|null>(null);
  const role = useAuthStore(s => s.user?.role);
  const canEditScopes = role === "admin" || role === "analyst";

  // Los filtros (área/laboratorio/estado/tipo) se aplican en cliente sobre el
  // array cargado, así que hay que traerse TODAS las páginas: quedarse en la
  // primera perdería filas y filtraría sólo sobre ellas.
  // Cada carga son N peticiones; si el usuario teclea en la búsqueda mientras
  // vuelan, una respuesta vieja podría pisar a la nueva. El contador descarta
  // los resultados de cargas ya superadas.
  const loadSeq = useRef(0);
  const load = async <T,>(path: string, apply: (items: T[], total: number) => void) => {
    const seq = ++loadSeq.current;
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (hier) params.set("hier", hier);
    const qs = params.toString() ? `?${params}` : "";
    const r = await api.getAll<T>(`${path}${qs}`);
    if (seq === loadSeq.current) apply(r.items, r.total);
  };
  const loadPF = () => load<PF>("/portfolio", (i, t) => { setItems(i); setPfTotal(t); });
  const loadComp = () => load<Comp>("/portfolio/competitors", (i, t) => { setComps(i); setCompTotal(t); });

  useEffect(() => { tab==="portfolio" ? loadPF() : loadComp(); }, [search, hier, tab]);

  const deletePF = async (id:string) => { await api.delete(`/portfolio/${id}`); loadPF(); };
  const deleteComp = async (id:string) => { await api.delete(`/portfolio/competitors/${id}`); loadComp(); };
  const confirmDelete = async () => {
    if(!pendingDelete) return;
    const { kind, id } = pendingDelete;
    setPendingDelete(null);
    try { kind==="pf" ? await deletePF(id) : await deleteComp(id); }
    catch(e:any) { setMsg(t("portfolio.errorPrefix")+e.message); setTimeout(()=>setMsg(""),3000); }
  };

  const confirmBulk = async () => {
    if (!pendingBulk) return;
    const { side, ids } = pendingBulk;
    setPendingBulk(null);
    try {
      const r = await api.post<{ removed: number }>("/portfolio/bulk-delete", { side, ids });
      side === "portfolio" ? loadPF() : loadComp();
      setMsg(t("portfolio.bulkRemoved", { n: r.removed })); setTimeout(()=>setMsg(""),4000);
    } catch(e:any) { setMsg(t("portfolio.errorPrefix")+e.message); setTimeout(()=>setMsg(""),4000); }
  };

  const submit = async () => {
    try {
      if(modal==="pf_add") { await api.post("/portfolio", form); loadPF(); }
      else { await api.post("/portfolio/competitors", form); loadComp(); }
      setModal(null); setForm({}); setMsg(t("portfolio.saved"));
      setTimeout(()=>setMsg(""),3000);
    } catch(e:any) { setMsg(t("portfolio.errorPrefix")+e.message); }
  };

  const heading = tab === "portfolio"
    ? t("portfolio.myProducts", { n: pfTotal })
    : t("portfolio.competitors", { n: compTotal });

  // Opciones de filtro derivadas de los productos cargados + lista filtrada (cliente).
  // Filtrado en cliente extraído a lib/portfolioFilters.ts (testeable).
  const areas = uniqueSorted(items, p => p.therapeutic_area);
  const labs = uniqueSorted(items, p => p.laboratory);
  // En «Mis Productos» lo normal es que el titular sea siempre el mismo (en
  // Italfarmaco, las 65 filas decían «Italfarmaco S.A.»): una columna con un
  // solo valor no informa y se lleva 170 px. Se muestra sólo si hay variedad,
  // que la puede haber con productos comercializados por otro titular.
  const showLab = labs.length > 1;
  const filteredItems = filterPortfolio(items, { area: areaFilter, lab: labFilter, status: statusFilter });

  const compAreas = uniqueSorted(comps, c => c.therapeutic_area);
  const compCompanies = uniqueSorted(comps, c => c.company_name);
  const filteredComps = filterCompetitors(comps, { type: compTypeFilter, area: compAreaFilter, company: compCompanyFilter });

  return (
    <div style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color:"var(--clr-text)", fontVariantNumeric:"tabular-nums" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <h1 style={{ margin:0, fontSize:"1.4rem", fontWeight:700, letterSpacing:"-0.02em" }}>{heading}</h1>
        <span style={{ display:"inline-flex", alignItems:"center", gap:12, flexWrap:"wrap", justifyContent:"flex-end" }}>
          {msg && <span style={{ color:msg.startsWith("Error")?"#f87171":"var(--clr-text3)", fontSize:"0.82rem" }}>{msg}</span>}
          {/* Contrato con Italfarmaco, punto 13: lo que ves, en Excel o CSV */}
          <ExportButtons small
            stem={tab === "portfolio" ? "helixscan_mis_productos" : "helixscan_competidores"}
            path={tab === "portfolio"
              ? `/export/portfolio?${new URLSearchParams({ search, hier, area: areaFilter, lab: labFilter, status: statusFilter })}`
              : `/export/competitors?${new URLSearchParams({ search, hier, type: compTypeFilter, area: compAreaFilter, company: compCompanyFilter })}`} />
        </span>
      </div>

      {/* Reglas de universo (§90 Fase 2B): lo que entra por laboratorio / PA / ATC / AH / SPR */}
      <ScopeRules side={tab === "portfolio" ? "portfolio" : "competitor"} canEdit={canEditScopes} />

      {/* Importaciones paralelas (feedback de Carlos, 20 oct 2026): competencia hasta que la org decida */}
      <ParallelImportsPanel canEdit={canEditScopes} onChanged={() => { tab === "portfolio" ? loadPF() : loadComp(); }} />

      <div style={{ background:"var(--clr-surface)", border:"1px solid var(--clr-border)", borderRadius:18, backdropFilter:"var(--clr-blur)", WebkitBackdropFilter:"var(--clr-blur)", boxShadow:"var(--clr-shadow-sm)", padding:"1rem 1.25rem" }}>
        <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
          <SearchInput value={search} onChange={setSearch} placeholder={t("portfolio.searchPlaceholder")}
            minWidth={220} style={{ borderRadius: 999, padding: "7px 14px", maxWidth: 300 }} />
          <HierarchyFilter value={hier} onChange={setHier} />
          {tab==="competitors" && (<>
            <select value={compTypeFilter} onChange={e=>setCompTypeFilter(e.target.value)} style={{ ...inp({ borderRadius:999 }), maxWidth:200, width:"auto" }}>
              <option value="">{t("portfolio.allTypes")}</option>
              <option value="direct">{t("portfolio.typeDirect")}</option>
              <option value="therapeutic">{t("portfolio.typeTherapeutic")}</option>
            </select>
            <select value={compCompanyFilter} onChange={e=>setCompCompanyFilter(e.target.value)} style={{ ...inp({ borderRadius:999 }), maxWidth:220, width:"auto" }}>
              <option value="">{t("portfolio.allLabs")}</option>
              {compCompanies.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <select value={compAreaFilter} onChange={e=>setCompAreaFilter(e.target.value)} style={{ ...inp({ borderRadius:999 }), maxWidth:220, width:"auto" }}>
              <option value="">{t("portfolio.allAreas")}</option>
              {compAreas.map(a=><option key={a} value={a}>{a}</option>)}
            </select>
          </>)}
          {tab==="portfolio" && (<>
            <select value={areaFilter} onChange={e=>setAreaFilter(e.target.value)} style={{ ...inp({ borderRadius:999 }), maxWidth:220, width:"auto" }}>
              <option value="">{t("portfolio.allAreas")}</option>
              {areas.map(a=><option key={a} value={a}>{a}</option>)}
            </select>
            {/* Con un único titular el filtro tampoco filtra nada: se oculta
                junto con la columna. */}
            {showLab && (
              <select value={labFilter} onChange={e=>setLabFilter(e.target.value)} style={{ ...inp({ borderRadius:999 }), maxWidth:220, width:"auto" }}>
                <option value="">{t("portfolio.allLabs")}</option>
                {labs.map(l=><option key={l} value={l}>{l}</option>)}
              </select>
            )}
            <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{ ...inp({ borderRadius:999 }), maxWidth:240, width:"auto" }}>
              <option value="">{t("portfolio.allStatuses")}</option>
              <option value="marketed">{t("catalog.marketed")}</option>
              <option value="notMarketed">{t("catalog.notMarketed")}</option>
              <option value="noPresentation">{t("portfolio.filterNoPresentation")}</option>
              <option value="co02">{t("portfolio.filterCo02")}</option>
            </select>
          </>)}
          {/* Bajas en bloque: solo con algún filtro puesto, para no vaciar la lista por error */}
          {canEditScopes && (() => {
            const pf = tab === "portfolio";
            const shown = pf ? filteredItems : filteredComps;
            const filtered = !!(search || hier || (pf ? (areaFilter || labFilter || statusFilter)
                                                      : (compTypeFilter || compAreaFilter || compCompanyFilter)));
            if (!filtered || shown.length === 0) return null;
            return (
              <button onClick={() => setPendingBulk({ side: pf ? "portfolio" : "competitor", ids: shown.map(x => x.id) })}
                style={{ marginLeft:"auto", background:"transparent", color:"#dc2626", border:"1px solid #dc262655", borderRadius:999,
                         padding:"6px 14px", fontSize:"0.78rem", fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}>
                {t(pf ? "portfolio.bulkRemoveProducts" : "portfolio.bulkRemoveCompetitors", { n: shown.length })}
              </button>
            );
          })()}
        </div>

        {tab==="portfolio" && (
          <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.82rem", tableLayout:"fixed", minWidth: tableMin }}>
            <thead><tr style={{ borderBottom:"1px solid var(--clr-border2)" }}>
              {([["CN",COL.cn],[t("portfolio.colProduct"),undefined],[t("portfolio.colActiveIngredient"),COL.ingredient],...(isMobile?[]:[[t("portfolio.colTherapeuticArea"),COL.area] as const,...(showLab?[[t("portfolio.colLaboratory"),COL.lab] as const]:[])]),[t("portfolio.colStatus"),COL.status],["",COL.actions]] as const)
                .map(([h,w],i)=><th key={`${h}-${i}`} style={{ ...thBase, width:w }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filteredItems.map(p=>(
                <tr key={p.id} style={{ borderTop:"1px solid var(--clr-border2)" }}>
                  <td style={{ padding:"0.6rem 10px", color:"var(--clr-text3)", fontSize:"0.78rem", whiteSpace:"nowrap" }}>{p.cn_code}</td>
                  <ProductNameCell
                    name={p.product_name}
                    title={t("portfolio.viewSheetOf", { name: p.product_name })}
                    onClick={() => setDetailCn(p.cn_code)}
                    icon={<OpenIcon />}
                  />
                  <td style={{ padding:"0.6rem 10px", color:"var(--clr-text2)", textTransform:"uppercase", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={p.active_ingredient}>{p.active_ingredient}</td>
                  {!isMobile && <td style={{ padding:"0.6rem 10px", color:"var(--clr-text3)", overflow:"hidden", whiteSpace:"nowrap" }} title={p.therapeutic_area||undefined}><AreaCell area={p.therapeutic_area} letter={p.atc_letter} /></td>}
                  {!isMobile && showLab && <td style={{ padding:"0.6rem 10px", color:"var(--clr-text3)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={p.laboratory||undefined}>{p.laboratory||"–"}</td>}
                  <td style={{ padding:"0.6rem 10px" }}>
                    <MarketedBadge marketed={p.is_comercializado} />
                    {p.sin_comercializar && (
                      /* CO-02 como atributo (catálogo 4.0): a los 3 años la autorización puede caducar. */
                      <div title={t(p.sin_comercializar.base === "cese" ? "portfolio.noPresentationCese" : "portfolio.noPresentationAut")}
                        style={{ marginTop: 3, fontSize: "0.68rem", lineHeight: 1.3, maxWidth: 170, fontWeight: p.sin_comercializar.meses >= 30 ? 700 : 500,
                                 color: p.sin_comercializar.meses >= 36 ? "#dc2626" : p.sin_comercializar.meses >= 30 ? "#d97706" : "var(--clr-text4)" }}>
                        {t("portfolio.noPresentationSince", { date: new Date(p.sin_comercializar.desde).toLocaleDateString(undefined, { timeZone: "UTC" }),
                                                            n: p.sin_comercializar.meses })}
                      </div>
                    )}
                  </td>
                  <td style={{ padding:"0.6rem 10px", textAlign:"center" }}>
                    <RowActionsMenu actions={[
                      { label:t("portfolio.actionViewSheet"), icon:<IconOpen />, onClick:()=>setDetailCn(p.cn_code) },
                      { label:t("portfolio.actionDeleteProduct"), icon:<IconTrash />, onClick:()=>setPendingDelete({kind:"pf", id:p.id}), danger:true },
                    ]} />
                  </td>
                </tr>
              ))}
              {filteredItems.length===0&&<tr><td colSpan={isMobile?5:7} style={{ padding:24, textAlign:"center", color:"var(--clr-text4)" }}>{items.length===0?t("portfolio.emptyProducts"):t("portfolio.emptyFiltered")}</td></tr>}
            </tbody>
          </table>
          </div>
        )}

        {tab==="competitors" && (
          <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.82rem", tableLayout:"fixed", minWidth: tableMin }}>
            <thead><tr style={{ borderBottom:"1px solid var(--clr-border2)" }}>
              {([["CN",COL.cn],[t("portfolio.colProduct"),undefined],[t("portfolio.colActiveIngredient"),COL.ingredient],...(isMobile?[]:[[t("portfolio.colTherapeuticArea"),COL.areaComp] as const,[t("portfolio.colLaboratory"),COL.labComp] as const]),[t("portfolio.colType"),COL.type],["",COL.actions]] as const)
                .map(([h,w],i)=><th key={`${h}-${i}`} style={{ ...thBase, width:w }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filteredComps.map(c=>(
                <tr key={c.id} style={{ borderTop:"1px solid var(--clr-border2)" }}>
                  <td style={{ padding:"0.6rem 10px", color:"var(--clr-text3)", fontSize:"0.78rem", whiteSpace:"nowrap" }}>{c.cn_code || "–"}</td>
                  <ProductNameCell
                    name={c.product_name}
                    title={c.product_name ? t("portfolio.viewSheetOf", { name: c.product_name }) : undefined}
                    onClick={c.cn_code ? () => setDetailCn(c.cn_code!) : undefined}
                    icon={<OpenIcon />}
                    color="var(--clr-text)"
                    fontWeight={500}
                  >
                    {c.origin === "market_entry" && (
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", padding:"0 8px 4px" }}>
                        <NewToMarketChip
                          label={t("portfolio.newToMarket")}
                          title={t("portfolio.newToMarketTooltip", {
                            ingredient: c.active_ingredient,
                            date: c.created_at ? new Date(c.created_at).toLocaleDateString() : "",
                          })}
                        />
                      </div>
                    )}
                    {c.origin === "importacion_paralela" && (
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", padding:"0 8px 4px" }}>
                        <span title={t("portfolio.parallelChipTooltip")} style={{
                          display:"inline-flex", alignItems:"center", background:"#d977061e", color:"#d97706",
                          border:"1px solid #d9770633", borderRadius:999, padding:"2px 9px", fontSize:"0.66rem",
                          fontWeight:600, whiteSpace:"nowrap" }}>{t("portfolio.parallelChip")}</span>
                      </div>
                    )}
                  </ProductNameCell>
                  <td style={{ padding:"0.6rem 10px", color:"var(--clr-text2)", textTransform:"uppercase", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={c.active_ingredient}>{c.active_ingredient}</td>
                  {!isMobile && <td style={{ padding:"0.6rem 10px", color:"var(--clr-text3)", overflow:"hidden", whiteSpace:"nowrap" }} title={c.therapeutic_area||undefined}><AreaCell area={c.therapeutic_area} letter={c.atc_letter} /></td>}
                  {!isMobile && <td style={{ padding:"0.6rem 10px", color:"var(--clr-text2)", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={c.company_name||undefined}>{c.company_name||"–"}</td>}
                  <td style={{ padding:"0.6rem 10px", whiteSpace:"nowrap" }}>
                    {/* Tipo = cápsula tintada suave SIN punto (el punto es de estados) */}
                    {(() => { const tc = c.competitor_type==="therapeutic" ? "#a78bfa" : "#f59e0b"; return (
                      <span style={{
                        display:"inline-flex", alignItems:"center",
                        background:tc+"1e", color:tc,
                        borderRadius:999, padding:"3px 11px",
                        fontSize:"0.72rem", fontWeight:600, whiteSpace:"nowrap",
                      }}>
                        {c.competitor_type==="therapeutic"?t("portfolio.typeTherapeutic"):t("portfolio.typeDirect")}
                      </span>
                    ); })()}
                  </td>
                  <td style={{ padding:"0.6rem 10px", textAlign:"center" }}>
                    <RowActionsMenu actions={[
                      ...(c.cn_code ? [{ label:t("portfolio.actionViewSheet"), icon:<IconOpen />, onClick:()=>setDetailCn(c.cn_code) }] : []),
                      { label:t("portfolio.actionDeleteCompetitor"), icon:<IconTrash />, onClick:()=>setPendingDelete({kind:"comp", id:c.id}), danger:true },
                    ]} />
                  </td>
                </tr>
              ))}
              {filteredComps.length===0&&<tr><td colSpan={isMobile?5:7} style={{ padding:24, textAlign:"center", color:"var(--clr-text4)" }}>{comps.length===0?t("portfolio.emptyCompetitors"):t("portfolio.emptyFilteredCompetitors")}</td></tr>}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {detailCn && <ProductDetailModal cn={detailCn} onClose={() => setDetailCn(null)} />}

      <ConfirmDialog
        open={pendingDelete!==null}
        title={pendingDelete?.kind==="comp" ? t("portfolio.confirmDeleteCompetitorTitle") : t("portfolio.confirmDeleteProductTitle")}
        message={t("portfolio.confirmDeleteMessage")}
        onConfirm={confirmDelete}
        onCancel={()=>setPendingDelete(null)}
      />
      <ConfirmDialog
        open={pendingBulk!==null}
        title={t(pendingBulk?.side==="competitor" ? "portfolio.bulkRemoveCompetitorsTitle" : "portfolio.bulkRemoveProductsTitle", { n: pendingBulk?.ids.length ?? 0 })}
        message={t("portfolio.bulkRemoveMessage")}
        onConfirm={confirmBulk}
        onCancel={()=>setPendingBulk(null)}
      />

      {/* Modal */}
      {modal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(11,18,21,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:999, backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)" }}>
          <div style={{ background:"var(--clr-modal-solid)", border:"1px solid var(--clr-border)", borderRadius:22, padding:"1.75rem", width:400, boxShadow:"0 24px 80px -24px rgba(0,0,0,0.45)" }}>
            <h3 style={{ margin:"0 0 1.25rem", color:"var(--clr-text)", fontSize:"1.05rem", fontWeight:700, letterSpacing:"-0.01em" }}>{modal==="pf_add"?t("portfolio.modalAddProduct"):t("portfolio.modalAddCompetitor")}</h3>
            {modal==="pf_add"&&<>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldCnRequired")}</span><input value={form.cn_code||""} onChange={e=>setForm(f=>({...f,cn_code:e.target.value}))} style={inp()} /></label>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldProductNameRequired")}</span><input value={form.product_name||""} onChange={e=>setForm(f=>({...f,product_name:e.target.value}))} style={inp()} /></label>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldActiveIngredientRequired")}</span><input value={form.active_ingredient||""} onChange={e=>setForm(f=>({...f,active_ingredient:e.target.value}))} style={inp()} /></label>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldLaboratory")}</span><input value={form.laboratory||""} onChange={e=>setForm(f=>({...f,laboratory:e.target.value}))} style={inp()} /></label>
              <label style={{ display:"block",marginBottom:16 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldTherapeuticArea")}</span><input value={form.therapeutic_area||""} onChange={e=>setForm(f=>({...f,therapeutic_area:e.target.value}))} style={inp()} /></label>
            </>}
            {modal==="comp_add"&&<>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldCn")}</span><input value={form.cn_code||""} onChange={e=>setForm(f=>({...f,cn_code:e.target.value}))} placeholder={t("portfolio.cnPlaceholder")} style={inp()} /></label>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldCompetitorType")}</span>
                <select value={form.competitor_type||"direct"} onChange={e=>setForm(f=>({...f,competitor_type:e.target.value}))} style={inp()}>
                  <option value="direct">{t("portfolio.competitorTypeDirect")}</option>
                  <option value="therapeutic">{t("portfolio.competitorTypeTherapeutic")}</option>
                </select>
              </label>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldCompany")}</span><input value={form.company_name||""} onChange={e=>setForm(f=>({...f,company_name:e.target.value}))} style={inp()} /></label>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldProduct")}</span><input value={form.product_name||""} onChange={e=>setForm(f=>({...f,product_name:e.target.value}))} style={inp()} /></label>
              <label style={{ display:"block",marginBottom:10 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldActiveIngredientRequired")}</span><input value={form.active_ingredient||""} onChange={e=>setForm(f=>({...f,active_ingredient:e.target.value}))} style={inp()} /></label>
              <label style={{ display:"block",marginBottom:16 }}><span style={{ fontSize:"0.72rem",color:"var(--clr-text3)",display:"block",marginBottom:4 }}>{t("portfolio.fieldTherapeuticArea")}</span><input value={form.therapeutic_area||""} onChange={e=>setForm(f=>({...f,therapeutic_area:e.target.value}))} style={inp()} /></label>
            </>}
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={()=>setModal(null)} style={{ background:"var(--clr-modal-solid2)", color:"var(--clr-text2)", border:"1px solid var(--clr-border2)", borderRadius:999, padding:"0.5rem 1.3rem", fontSize:"0.8rem", fontWeight:600, cursor:"pointer" }}>{t("portfolio.cancel")}</button>
              <button onClick={submit} style={btn("var(--clr-org-primary)")}>{t("portfolio.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
